import { AlertTriangle, FileQuestion, Lock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { familyOf, LINK_STATUS, LINK_TONE_CLASSES } from '../config/families';
import { DocLinkStatus } from '../types/documentCapture.types';

/**
 * Family + link-status chips (86d3rrfbf), ported from the `.dc.html` prototype.
 *
 * Both carry a tooltip: the family chip because the prefix alone doesn't tell a new user what
 * "INT" is or that it's restricted, and the link chip because its copy IS the user education for
 * link status — especially "Waiting for NetSuite", which must never read as an error.
 */

interface DocTypeChipProps {
  /** Document number or bare prefix. */
  value: string | null | undefined;
  className?: string;
}

export function DocTypeChip({ value, className }: DocTypeChipProps) {
  const family = familyOf(value);

  // Unregistered / not yet identified. Deliberately neutral — an unclassified scan is treated as
  // maximally sensitive, so its chip must not hint at a family.
  if (!family) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full border border-border bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground',
              className
            )}
          >
            Unclassified
          </span>
        </TooltipTrigger>
        <TooltipContent>The barcode reader hasn’t identified this scan yet.</TooltipContent>
      </Tooltip>
    );
  }

  const Icon = family.icon;
  const isTreasury = family.tier === 'treasury';

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {/* Categorical identity hue via a custom property — see FamilyConfig.hue for why this is
            a deliberate exception to semantic-token-only. */}
        <span
          style={{ '--fam': family.hue } as React.CSSProperties}
          className={cn(
            'inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium',
            'border-[hsl(var(--fam)/0.28)] bg-[hsl(var(--fam)/0.12)] text-[hsl(var(--fam))]',
            className
          )}
        >
          <Icon className="h-3 w-3 flex-shrink-0" aria-hidden />
          {family.prefix}
          {isTreasury && <Lock className="h-2.5 w-2.5 flex-shrink-0 opacity-70" aria-hidden />}
        </span>
      </TooltipTrigger>
      <TooltipContent>
        {family.label}
        {isTreasury && ' · Treasury — restricted, every read is audited'}
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * The rounded-square icon tile the prototype uses wherever a document or scan is *listed* — the
 * needs-review rail, the review queue, the review identity header, the registry's Document column.
 *
 * It exists because those lists show a tile, not a `DocTypeChip`: a chip is a wide text pill that
 * pushes the number off the row and reads as a filter you could click. The tile is a fixed square
 * that leaves the number as the row's first readable thing. Building T13 without opening the
 * prototype is exactly how chips ended up in all four places.
 *
 * `tone`:
 *  - `family` — tinted with the family's categorical hue (registry, review queue). Unregistered
 *    series fall back to muted + a question-mark page, never to a family colour.
 *  - `warning` — every tile amber regardless of family (the needs-review rail). There the row's
 *    subject is the *exception*, not the document type; the glyph still names the family.
 */
interface DocIconTileProps {
  /** Document number or bare prefix; null/unregistered renders the neutral fallback. */
  value: string | null | undefined;
  tone?: 'family' | 'warning';
  /** Tile edge in px, and its corner radius — both vary by surface in the prototype. */
  size: number;
  radius: number;
  /** Glyph edge in px. Deliberately explicit: the prototype's ratios are not uniform. */
  icon: number;
  className?: string;
}

export function DocIconTile({ value, tone = 'family', size, radius, icon, className }: DocIconTileProps) {
  const family = familyOf(value);
  // Amber rail tiles keep the family glyph but drop to a warning triangle when nothing is known —
  // "no idea what this is" is itself the exception being reported.
  const Icon = family?.icon ?? (tone === 'warning' ? AlertTriangle : FileQuestion);

  const tinted = tone === 'family' && family;

  return (
    <span
      aria-hidden
      style={{
        width: size,
        height: size,
        borderRadius: radius,
        ...(tinted
          ? ({
              '--fam': family.hue,
              background: 'hsl(var(--fam)/0.13)',
              color: 'hsl(var(--fam))',
            } as React.CSSProperties)
          : {}),
      }}
      className={cn(
        'inline-flex flex-shrink-0 items-center justify-center',
        tone === 'warning' && 'bg-warning/[0.14] text-warning',
        tone === 'family' && !family && 'bg-muted text-muted-foreground',
        className
      )}
    >
      <Icon style={{ width: icon, height: icon }} />
    </span>
  );
}

interface LinkStatusChipProps {
  status: DocLinkStatus;
  className?: string;
  /**
   * 86d45ffw4 — when a person pasted the link into NetSuite by hand, and who. Optional: callers with
   * only a status (the live stream's duplicate badge) pass neither and get the plain tooltip.
   */
  manualLinkedAt?: string | null;
  manualLinkedByName?: string | null;
}

export function LinkStatusChip({ status, className, manualLinkedAt, manualLinkedByName }: LinkStatusChipProps) {
  const config = LINK_STATUS[status] ?? LINK_STATUS[DocLinkStatus.Pending];
  const Icon = config.icon;

  /*
    86d45ffw4 — the human provenance, appended rather than replacing the status copy.

    The two cases say DIFFERENT things and must not be collapsed. On a ManuallyLinked chip the stamp
    is the claim itself, so it names who to ask. On a LINKED chip the stamp has survived the sweep's
    promotion, which means DOP has since seen the link on the record — the claim was true, and saying
    so is what stops the row looking like an ordinary automatic link when it wasn't.

    The design said "Confirmed by you". A registry row is read by everyone, so it names the person.
  */
  const who = manualLinkedByName ? ` by ${manualLinkedByName}` : '';
  const when = manualLinkedAt ? new Date(manualLinkedAt).toLocaleString() : '';
  const manualNote = !manualLinkedAt
    ? ''
    : status === DocLinkStatus.ManuallyLinked
      ? ` Confirmed${who} on ${when}.`
      : status === DocLinkStatus.Linked
        ? ` Pasted in by hand${who} on ${when}, and since seen on the record.`
        : '';
  const tip = `${config.tip}${manualNote}`;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn(
            'inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium',
            LINK_TONE_CLASSES[config.tone],
            className
          )}
        >
          <Icon
            className={cn(
              'h-3 w-3 flex-shrink-0',
              // Self-resolving state gets a gentle pulse so it reads as "in progress", not stuck.
              // Killed under reduced-motion.
              config.pulse && 'motion-safe:animate-pulse'
            )}
            aria-hidden
          />
          {config.label}
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">{tip}</TooltipContent>
    </Tooltip>
  );
}
